import React from 'react';
import './styles.css'
import DmsConnectivity from '../pages/dms_connectivity';
import LabelConfig from '../pages/label_config';
import Preferences from '../pages/preferences';
import EnableQR from '../pages/enableQR';
import AddVehicles from '../pages/addVehicle';
import { AlertsData } from '../pages/dashboard';
import AddScannedVehicle from '../pages/addScannedVehicles';
import ReportSummary from '../pages/reportSummary';
import SaveGridModal from '../pages/saveGrid';
import ClearGrid from '../pages/clearGrid';
import PrintTemplateSelector from '../pages/printTemplateSelector';
import RecentDownloadsModal from '../pages/recentDownloads';
import InventorySelection from '../pages/inventorySelection';
import ColumnChooser from '../pages/columnChooser';
import LotLocationChange from '../pages/lotLocation';
import UserForms from '../pages/userForms';
import AddendumVehicleForm from '../pages/AddendumForm';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    onAlert: AlertsData;
    onHideData: any;
}

// Modal configuration for different modal types
const MODAL_CONFIG = {
    'DMS Connectivity': {
        className: 'modal-content w-[70%] h-[70%] overflow-y-scroll',
        component: (props: ComponentProps) => <DmsConnectivity onAlertChnage={props.onAlert} />
    },
    'Label Request Information': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <LabelConfig onAlertChnage={props.onAlert} />
    },
    'Preferences': {
        className: 'modal-content w-[70%]',
        component: (props: ComponentProps) => <Preferences onAlertChnage={props.onAlert} />
    },
    'Recent Downloads': {
        className: 'modal-content w-[70%]',
        component: () => <RecentDownloadsModal />
    },
    'Print Data': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <PrintTemplateSelector onAlertChnage={props.onAlert} title={props.title} />
    },
    'ExtraVehPrint': {
        className: 'modal-content w-[100%]',
        component: (props: ComponentProps) => <PrintTemplateSelector onAlertChnage={props.onAlert} title={props.title} />
    },
    'columnChooser': {
        className: 'modal-content w-[30%] h-[40%] overflow-y-scroll',
        component: (props: ComponentProps) => <ColumnChooser onHideDataChange={props.onHideData} />
    },
    'User Form': {
        className: 'modal-content w-[60%] h-[70%] overflow-y-scroll',
        component: () => <UserForms />
    },
    'Enable QR-Code': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <EnableQR onAlertChnage={props.onAlert} />
    },
    'Add Vehicles': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <AddVehicles onAlertChnage={props.onAlert} onClose={props.onClose} />
    },
    'scanVehicles': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <AddScannedVehicle onAlertChnage={props.onAlert} />
    },
    'Report Summary': {
        className: 'modal-content w-[40%]',
        component: () => <ReportSummary />
    },
    'saveGrid': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <SaveGridModal onAlertChnage={props.onAlert} />
    },
    'clearGrid': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <ClearGrid onAlertChnage={props.onAlert} />
    },
    'Select Inventory': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <InventorySelection onAlertChnage={props.onAlert} onClose={props.onClose} title={props.title} />
    },
    'End Inventory': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <InventorySelection onAlertChnage={props.onAlert} onClose={props.onClose} title={props.title} />
    },
    'changeLotLocation': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <LotLocationChange onAlertChnage={props.onAlert} />
    },
    'Addendum': {
        className: 'modal-content w-[40%]',
        component: (props: ComponentProps) => <AddendumVehicleForm onClose={props.onClose} onAlertChnage={props.onAlert} />
    }
} as const;

// Empty modals that render nothing
const EMPTY_MODALS = [
    'Download Label Print Support',
    'User List',
    'Vehicle Status'
] as const;

interface ComponentProps {
    onAlert: AlertsData;
    onClose: () => void;
    title: string;
    onHideData: any;
}

const CommonModal: React.FC<ModalProps> = ({ isOpen, onClose, title, onAlert, onHideData }) => {
    if (!isOpen) return null;

    // Check if it's an empty modal
    if (EMPTY_MODALS.includes(title as any)) {
        return (
            <div className="modal">
                <div className="modal-content w-[40%]">
                    <ModalHeader title={title} onClose={onClose} />
                    <div />
                </div>
            </div>
        );
    }

    // Get modal configuration
    const config = MODAL_CONFIG[title as keyof typeof MODAL_CONFIG];
    
    if (!config) {
        console.warn(`No configuration found for modal title: ${title}`);
        return (
            <div className="modal">
                <div className="modal-content w-[40%]">
                    <ModalHeader title={title} onClose={onClose} />
                    <div>Modal content not configured</div>
                </div>
            </div>
        );
    }

    const ComponentToRender = config.component;

    return (
        <div className="modal">
            <div className={config.className}>
                <ModalHeader title={title} onClose={onClose} />
                <ComponentToRender 
                    onAlert={onAlert}
                    onClose={onClose}
                    title={title}
                    onHideData={onHideData}
                />
            </div>
        </div>
    );
};

// Extracted header component for reusability
const ModalHeader: React.FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => (
    <>
        <div className="top-container">
            <span className="close" onClick={onClose}>
                &times;
            </span>
            <p className="headerTitle capitalize">{title}</p>
        </div>
        <hr />
    </>
);

export default CommonModal;